import { SubjectsBadge } from "@/components/SubjectsBadge";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { subjectList } from "@/lib/subjectChoices";
import { readApplicantExplicit, readApplicantProfile } from "@/lib/applicantProfile";
import AdvancedTeaser from "./AdvancedTeaser";
import AdvancedWizard from "./AdvancedWizard";
import AdvancedResults from "./AdvancedResults";

// Anon "3 free matches" session storage keys — the profile the user
// built in the wizard lives client-side only (never persisted to
// db.profiles), and the wizard uses `wua:anon-profile` to remember
// their answers if they leave and come back.
const ANON_PROFILE_KEY = "wua:anon-profile";

function readAnonProfile() {
  try {
    const raw = sessionStorage.getItem(ANON_PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeAnonProfile(p) {
  try { sessionStorage.setItem(ANON_PROFILE_KEY, JSON.stringify(p)); } catch {}
}

export default function AdvancedSearch() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const focusDegreeId = params.get("focus");
  // "Flagship-50 preset" — /advanced?preset=flagships lets a visitor
  // land straight on the hand-curated editorial catalogue (NL WO-14,
  // FR Top-12, DE Top-12, US Ivy Plus) without running the wizard.
  // We stub a permissive profile so the anon match doesn't 400 on
  // missing intended-degree / curriculum, and pass the verified-only
  // flag down so `AdvancedResults` initialises its trust-tier chip ON.
  const flagshipPreset = (params.get("preset") || "").toLowerCase() === "flagships";
  // Dream-employer preselection — the /how-to-get-into/{firm} guides
  // deep-link here with `?dream_employer=Goldman%20Sachs` so the visitor
  // arrives with the firm already targeted. We fold it into the wizard
  // seed (anon path) or PATCH the server profile (signed-in path)
  // before choosing which view to render.
  const dreamEmployerFromUrl = (params.get("dream_employer") || "").trim();
  // Subject guides deep-link with `?subject=Law`; it is added to the subject chips and the wizard opens to confirm.
  const subjectFromUrl = (params.get("subject") || "").trim().slice(0, 80);
  const addSubject = (p) => {
    const cur = subjectList([...subjectList(p?.intended_degree), ...subjectList(p?.intended_degrees)]);
    if (!subjectFromUrl || cur.some((x) => x.toLowerCase() === subjectFromUrl.toLowerCase())) return null;
    const { intended_degrees: _drop, ...rest } = p || {};
    return { ...rest, intended_degree: cur.length ? [...cur, subjectFromUrl] : subjectFromUrl };
  };
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [view, setView] = useState("teaser");   // teaser | wizard | results

  useEffect(() => {
    if (loading) return;

    // Flagship preset — skip both teaser and wizard, jump straight to
    // results with a permissive stub profile (so anon-match doesn't
    // reject on missing intended_degree / curriculum) and the trust-
    // tier chip pre-toggled ON.
    if (flagshipPreset) {
      // Permissive stub — the anon endpoint requires curriculum + either
      // intended_degree or curriculum-and-grades. We ship a bland
      // curriculum + grades so the payload validates, but omit
      // intended_degree entirely so the subject pre-filter is skipped
      // (the engine treats an empty intent as "match any subject").
      // The `verified_only=true` param does the heavy lifting server-side.
      const stubProfile = {
        curriculum: "a-level",
        predicted_grades: { "a-level": "ABB" },
        preferred_countries: [],
        psw_importance: 3,
      };
      if (!user) writeAnonProfile(stubProfile);
      setProfile(stubProfile);
      setView("results");
      return;
    }

    // Anonymous flow — 3 free matches without an account. If the visitor
    // has an anon profile in sessionStorage (i.e. already ran ≥1 match
    // in this session) drop straight into results; otherwise show the
    // teaser + let them opt in to the free trial.
    if (!user) {
      let anon = readAnonProfile();
      // If a URL-provided dream employer isn't already on the anon
      // profile, merge it in. Always show the wizard when the anon
      // profile is incomplete, so the visitor sees the pre-selected
      // firm chip before they run the match.
      if (dreamEmployerFromUrl) {
        anon = anon || {};
        const dreams = Array.isArray(anon.dream_employers) ? anon.dream_employers : [];
        if (!dreams.some((d) => d.toLowerCase() === dreamEmployerFromUrl.toLowerCase())) {
          anon = { ...anon, dream_employers: [...dreams, dreamEmployerFromUrl] };
        }
        writeAnonProfile(anon);
      }
      const withSubject = addSubject(anon);
      if (withSubject) {
        setProfile(withSubject);
        setView("wizard");
        return;
      }
      if (anon && anon.curriculum && (anon.intended_degree || anon.intended_degrees)) {
        setProfile(anon);
        setView("results");
      } else if (anon && (anon.dream_employers || []).length > 0) {
        // Firm-guide funnel: land straight in the wizard with the
        // employer chip already picked, don't force them through the
        // marketing teaser one more time.
        setProfile(anon);
        setView("wizard");
      } else {
        setView("teaser");
      }
      return;
    }
    (async () => {
      setProfileLoading(true);
      try {
        const { data } = await api.get("/profile");
        // Signed-in dream-employer merge: if the URL brought a firm the
        // profile doesn't already list, PATCH it in before scoring.
        let merged = data || {};
        if (dreamEmployerFromUrl) {
          const cur = Array.isArray(merged.dream_employers) ? merged.dream_employers : [];
          if (!cur.some((d) => d.toLowerCase() === dreamEmployerFromUrl.toLowerCase())) {
            const next = [...cur, dreamEmployerFromUrl];
            try {
              const { data: saved } = await api.post("/profile", { dream_employers: next });
              merged = saved?.profile || { ...merged, dream_employers: next };
            } catch {
              // Non-fatal — fall through to the vanilla flow.
            }
          }
        }
        // "Complete profile" = we actually have *something* to match on.
        // Previous check (`intended_degree !== undefined`) accepted `null`
        // and `""`, both of which fall through to the backend's full
        // 144k-degree scan and have been correlated with pod restarts.
        // Require a real curriculum AND some form of subject intent
        // (singular legacy field or the new array) before we send the
        // user to results — otherwise bounce them to the wizard.
        // intended_degree is a string for one subject, a list for several.
        const withSubject = addSubject(merged);
        if (withSubject) {
          setProfile(withSubject);
          setView("wizard");
          return;
        }
        const hasSubjectIntent =
          subjectList(merged?.intended_degree).length > 0 || subjectList(merged?.intended_degrees).length > 0;
        const hasCompleteProfile = !!(merged && merged.curriculum && hasSubjectIntent);
        if (hasCompleteProfile) {
          setProfile(merged);
          setView("results");
          return;
        }

        // Fast-path: user arrived here from Quick Check "See the full
        // picture" — they have a target course in mind (?focus=X) AND
        // they've already filled the QC form (sessionStorage snapshot).
        // Auto-persist that snapshot as a partial server profile so we
        // can drop them straight into results scoped to their target,
        // rather than forcing them through the wizard homepage again.
        const qc = readApplicantProfile();
        const hasQc =
          qc &&
          qc.curriculum &&
          (subjectList(qc.intended_degrees).length > 0 || subjectList(qc.intended_degree).length > 0);
        if (focusDegreeId && hasQc) {
          // Normalise plural → singular for the legacy server field.
          const patch = { ...qc };
          if (Array.isArray(patch.intended_degrees) && patch.intended_degrees.length) {
            patch.intended_degree = patch.intended_degrees[0];
            delete patch.intended_degrees;
          }
          // Only what the student entered in Quick Check counts as their answer; defaults don't.
          patch.explicit_fields = readApplicantExplicit()
            .map((k) => (k === "intended_degrees" ? "intended_degree" : k))
            .filter((k) => k in patch);
          patch.source_surface = "quick_check";
          try {
            const { data: saved } = await api.post("/profile", patch);
            setProfile(saved?.profile || saved || patch);
            setView("results");
            return;
          } catch {
            // Fall through to wizard if the auto-save fails.
          }
        }

        // Seed the wizard with whatever we already have (including the
        // merged dream_employer) so the chip is visible immediately.
        setProfile(merged);
        setView("wizard");
      } catch {
        setView("wizard");
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [user, loading, focusDegreeId, dreamEmployerFromUrl, subjectFromUrl, flagshipPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  // While auth/profile are still resolving, render nothing — the flash is
  // typically < 300ms and a top-left "Loading…" placeholder is more jarring
  // than a blank frame.
  if (loading || profileLoading) {
    return null;
  }

  if (view === "teaser") {
    return (
      <>
      <div className="mx-auto max-w-7xl px-6 pt-6 lg:px-10"><SubjectsBadge /></div>
      <AdvancedTeaser
        focusDegreeId={focusDegreeId}
        onStartFreeTrial={() => setView("wizard")}
      />
      </>
    );
  }
  if (view === "wizard") {
    return (
      <>
      <div className="mx-auto max-w-7xl px-6 pt-6 lg:px-10"><SubjectsBadge /></div>
      <AdvancedWizard
        anonymous={!user}
        onSaved={(p) => {
          if (!user) writeAnonProfile(p);
          setProfile(p);
          setView("results");
        }}
        initial={profile}
      />
      </>
    );
  }
  return (
    <>
    <div className="mx-auto max-w-7xl px-6 pt-6 lg:px-10"><SubjectsBadge /></div>
    <AdvancedResults
      profile={profile}
      focusDegreeId={focusDegreeId}
      anonymous={!user}
      onEdit={() => setView("wizard")}
      onProfileSaved={setProfile}
      initialVerifiedOnly={flagshipPreset}
      flagshipOnly={flagshipPreset}
    />
    </>
  );
}
